/* ============================================================
   PICTURE GOODS STORE — Shopify 本番用スクリプト
   - 画像選択 → 即時ローカルプレビュー（全モックアップ反映）
   - Cloudflare Workers へアップロード（R2 保存）→ 公開 URL を取得
   - 解像度（DPI相当）自動チェック
   - ?image= / ?client= クエリ契約（パートナーサイト経由も従来どおり）
   - SKU → バリアントID 解決（/products/{handle}.js）
   - Shopify Ajax Cart（/cart/add.js, /cart.js, /cart/change.js）
   設定は window.PGS（テーマ側 Liquid が出力）から読み込む:
     PGS.workerEndpoint … 任意。アップロード先 Cloudflare Workers の URL。
                          未指定なら下記 DEFAULT_WORKER_ENDPOINT を使う
     PGS.gasEndpoint   … GAS WebアプリURL（/exec）。旧 Drive 画像の読み出し専用
     PGS.defaultClient … クエリに client が無い場合の販売元名
     PGS.sellers       … metaobject seller の name 一覧（Liquid 出力）
     PGS.urls          … {lp, products, guide, faq, cart}
     PGS.uploadRetryAttempts … 任意。アップロードの試行回数（既定2＝再試行1回）
                               1 を指定すると再試行しない

   ■ 2026-09 通信安定化改修
     1. 全ての GAS 呼び出しを gasRequest() に集約し、AbortController による
        タイムアウトと、指数バックオフ＋ジッターによる自動再試行を導入。
        従来は fetch 一発勝負だったため、応答待ちの間に回線が切れると
        （GAS 側は完走して Drive に保存済みでも）そのまま失敗表示になっていた。
     2. ?image= 復元で要求する縮小サイズを 2000 → 1600 に変更。
        Drive の thumbnailLink は概ね 1600px までしか安定して返らず、
        超過すると GAS が原本（base64で約27MB）を返す低速経路に落ちていた。
     3. 取得済みの縮小版 dataURL を sessionStorage にも保持し、ページ遷移の
        たびに GAS を叩き直さないようにした（画像の引き継ぎ失敗対策）。
     4. エラー文言を実態に合わせて修正（従来はデプロイ設定の確認を促していたが、
        実際にはネットワーク起因のため切り分けを誤らせていた）。

   ■ 2026-09-24 アップロード先を GAS/Drive → Cloudflare Workers/R2 へ変更
     参照実装: _reference/cloudflare_upload_test.html
     1. 画像の保存は multipart/form-data（FormData に File をそのまま append）で
        Workers へ POST し、レスポンス { success:true, url } の url を採用する。
        base64（JSON）で送っていた従来方式は転送量が約 4/3 に膨らみ、
        送信前の変換にも時間がかかっていた。File を直接送ることで両方が解消する。
     2. R2 の公開 URL は Drive と違い Cross-Origin-Resource-Policy の制限が無く、
        <img src> にそのまま指定できる。?image= 復元時の「GAS 経由で dataURL 化」
        という往復が不要になり、画像の引き継ぎが即座に完了する。
     3. canvas 加工（丸型切り抜き・マグのワープ・モック撮影）だけは CORS クリーンな
        ピクセルが要るため、必要になった時点で fetch → Blob → dataURL 化する。
        CORS が許可されていない場合も URL のまま返して表示だけは成立させる。
     4. 旧 Drive URL（既存カート・配布済みリンク）の読み出しは従来どおり
        GAS プロキシで継続する。新規の保存先は R2 のみ。
   ============================================================ */
(function () {
  "use strict";

  var CFG = window.PGS || {};

  /* ---------- アップロード先（Cloudflare Workers → R2） ----------
     Worker は multipart/form-data の "file" を受け取り、
     { success: true, url: "<R2の公開URL>" } を返す契約
     （失敗時は { success: false, error: "..." }）。
     テーマ側から差し替えたい場合は window.PGS.workerEndpoint を出力する。 */
  /*var DEFAULT_WORKER_ENDPOINT = "https://image-uploader-api.muah6666.workers.dev/";*/
  var DEFAULT_WORKER_ENDPOINT = "https://pgs-image-uploader-api.contract-2e1.workers.dev/";
  var WORKER_ENDPOINT = CFG.workerEndpoint || DEFAULT_WORKER_ENDPOINT;
  /* 配信中のファイルが新旧どちらか、コンソールで即判別するための目印。
     旧版には存在しないため、undefined なら古い pgs.js が読まれている。 */
  window.PGS_BUILD = "2026-09-24 r2-upload";

  /* ---------- サンプル画像（アップロード前のデモ表示用・SVG生成） ---------- */
  var SAMPLE_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300">' +
    '<defs><linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">' +
    '<stop offset="0" stop-color="#8ED0F0"/><stop offset=".65" stop-color="#DDF1FB"/></linearGradient>' +
    '<linearGradient id="sea" x1="0" y1="0" x2="0" y2="1">' +
    '<stop offset="0" stop-color="#2E9CCB"/><stop offset="1" stop-color="#0F6E9E"/></linearGradient></defs>' +
    '<rect width="400" height="300" fill="url(#sky)"/>' +
    '<circle cx="308" cy="72" r="34" fill="#FFD65C"/>' +
    '<path d="M0 190 L90 120 L150 176 L214 108 L290 182 L400 132 L400 300 L0 300 Z" fill="#3E7A98" opacity=".55"/>' +
    '<rect y="196" width="400" height="104" fill="url(#sea)"/>' +
    '<g stroke="#BDE6F7" stroke-width="4" stroke-linecap="round" opacity=".7">' +
    '<path d="M40 226 h52"/><path d="M150 248 h64"/><path d="M262 230 h48"/><path d="M96 268 h58"/></g>' +
    '<g fill="#FFFFFF" opacity=".9"><ellipse cx="96" cy="66" rx="34" ry="13"/><ellipse cx="124" cy="58" rx="26" ry="11"/>' +
    '<ellipse cx="216" cy="46" rx="30" ry="11"/></g></svg>';
  var SAMPLE_SRC = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(SAMPLE_SVG);
  /* テーマアセットのサンプル写真（pgs-head.liquid が window.PGS.sampleImage で供給）。
     未設定時は従来のSVGサンプルにフォールバック */
  if (CFG.sampleImage) SAMPLE_SRC = CFG.sampleImage;

  /* ---------- 状態 ----------
     previewSrc … 画面表示用（dataURL または Drive URL）
     hostedUrl  … Drive 上の共有URL（カートの line item property に入れる値）
     client     … 販売元名（クエリ or 既定値）
  ---------------------------------- */
  var state = {
    previewSrc: SAMPLE_SRC,
    hostedUrl: "",
    fileName: null,
    client: "",
    uploading: false,
    roundUrl: "",  /* 丸型トリミング済み画像の Drive URL（丸型キーホルダー専用） */
    roundDataUrl: "" /* 同・即時表示／canvas合成用の dataURL（GAS往復を避けるため保持） */
  };
  /* FileReader / 画像変形 / GAS upload は非同期で完了するため、画像を選び直した
     あとの古い処理結果が最新画像を上書きしないよう世代番号で識別する。 */
  var imageRevision = 0;

  /* 丸型エディタ（pgs-round-editor.js）へ現在のプレビュー画像を公開。
     state.previewSrc は ?image= 復元時、画面表示用に縮小した dataURL になって
     いることがあるため、原本の解像度が必要なエディタには state.hostedUrl
     （Drive の直URL。PGSRemote 経由で原本を取得し直す）を優先して渡す。 */
  function publishPreview() {
    window.PGSPreview = { src: state.hostedUrl || state.previewSrc, fileName: state.fileName };
  }

  /* ============================================================
     計測（デバッグ用）
     有効化: URL に ?pgsdebug=1 を付ける、または
             localStorage.setItem("pgsDebug", "1")
     無効時は一切の出力を行わず、オーバーヘッドもほぼ無い。
     ============================================================ */
  var PGS_DEBUG = (function () {
    try {
      if (/[?&]pgsdebug=1\b/.test(window.location.search)) return true;
      return window.localStorage && window.localStorage.getItem("pgsDebug") === "1";
    } catch (e) { return false; }
  })();

  var marks = [];
  /* 計測開始（クリックごとにリセット） */
  function markReset(label) {
    if (!PGS_DEBUG) return;
    marks = [{ label: label || "start", at: performance.now() }];
  }
  /* 区間の記録 */
  function mark(label) {
    if (!PGS_DEBUG || !marks.length) return;
    marks.push({ label: label, at: performance.now() });
  }
  /* サーバ側が timing を返した場合に取り込む（Worker / 旧GAS 共通） */
  function markServerTiming(prefix, timing) {
    if (!PGS_DEBUG || !timing) return;
    marks.push({ label: prefix + " [server]", gas: timing });
  }
  /* 集計して console.table に出力 */
  function markDump(title) {
    if (!PGS_DEBUG || marks.length < 2) return;
    var rows = [];
    var base = marks[0].at;
    var prev = base;
    marks.forEach(function (m, i) {
      if (m.gas) {
        rows.push({
          step: m.label,
          "区間(ms)": "-",
          "累計(ms)": "-",
          "GAS内訳(ms)": JSON.stringify(m.gas)
        });
        return;
      }
      rows.push({
        step: m.label,
        "区間(ms)": i === 0 ? 0 : Math.round(m.at - prev),
        "累計(ms)": Math.round(m.at - base),
        "GAS内訳(ms)": ""
      });
      prev = m.at;
    });
    /* eslint-disable no-console */
    console.groupCollapsed("[PGS] " + (title || "timing") + " — 合計 " + Math.round(prev - base) + "ms");
    console.table(rows);
    console.groupEnd();
    /* eslint-enable no-console */
    marks = [];
  }

  /* ---------- helpers ---------- */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function driveFileIdFromUrl(url) {
    var value = String(url || "");
    var match = value.match(/[?&]id=([\w-]+)/);
    if (!match) match = value.match(/drive\.google\.com\/file\/d\/([\w-]+)/);
    if (!match) match = value.match(/googleusercontent\.com\/d\/([\w-]+)/);
    return match ? match[1] : "";
  }
  function originalDriveDownloadUrl(id) {
    return "https://drive.usercontent.google.com/download?id=" +
      encodeURIComponent(id) + "&export=download&confirm=t";
  }
  function normalizeHostedImageUrl(url) {
    var value = String(url || "");
    var id = driveFileIdFromUrl(value);
    if (!id) return value;

    /* thumbnail / uc / lh3 の表示用URLを、原本ファイルのダウンロードURLへ統一する。 */
    if (/^https:\/\/(?:drive|docs)\.google\.com\//i.test(value) ||
        /^https:\/\/(?:drive\.)?usercontent\.google\.com\//i.test(value) ||
        /^https:\/\/lh3\.googleusercontent\.com\//i.test(value)) {
      return originalDriveDownloadUrl(id);
    }
    return value;
  }
  /* 旧 Drive 由来の URL かどうか（true なら GAS プロキシ経由で読み出す） */
  function isDriveUrl(url) {
    return !!driveFileIdFromUrl(url);
  }

  /* ---------- dataURL ↔ Blob ----------
     アップロードは FormData（＝バイナリのまま）で送るため、
     canvas が吐く dataURL は送信前に Blob へ戻す。 */
  function dataUrlToBlob(dataUrl) {
    var parts = String(dataUrl || "").split(",");
    var mime = (String(parts[0] || "").match(/^data:([^;,]+)/) || [])[1] || "application/octet-stream";
    var binary = atob(parts[1] || "");
    var bytes = new Uint8Array(binary.length);
    for (var i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  }

  function blobToDataUrl(blob) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onload = function () { resolve(reader.result); };
      reader.onerror = function () { reject(new Error("画像を読み込めませんでした")); };
      reader.readAsDataURL(blob);
    });
  }

  /* canvas → Blob。toBlob 非対応環境では dataURL 経由にフォールバックする。 */
  function canvasToBlob(canvas, mimeType, quality) {
    return new Promise(function (resolve, reject) {
      if (canvas.toBlob) {
        /* tainted canvas の場合は同期的に SecurityError を投げる（＝reject される） */
        canvas.toBlob(function (blob) {
          if (blob) resolve(blob);
          else reject(new Error("画像を変換できませんでした"));
        }, mimeType, quality);
        return;
      }
      resolve(dataUrlToBlob(canvas.toDataURL(mimeType, quality)));
    });
  }

  function yen(minor) {
    return new Intl.NumberFormat("ja-JP", { style: "currency", currency: "JPY" }).format(minor / 100);
  }

  /* ---------- 処理中モーダル ----------
     全画面を覆い、背景を inert にしてマウス・キーボード操作を一時停止する。
     カート追加時の「丸型切り抜きの生成・保存」と「モック合成プレビューの
     生成・保存」の両方で使うため、見出しと説明文は呼び出し側から渡す。
     表示中に再度呼ぶと、モーダルを出したまま文言だけ差し替える。 */
  var PREPARING_DEFAULT_TITLE = "プレビュー画像を準備中…";
  var PREPARING_DEFAULT_MESSAGE = "処理中です。完了するまで、そのままお待ちください。";
  var previewPreparingUi = null;
  function setPreviewPreparing(isPreparing, title, message) {
    if (!previewPreparingUi) {
      var style = document.createElement("style");
      style.textContent =
        ".pgs-processing-overlay{position:fixed;inset:0;z-index:2147483647;display:none;align-items:center;justify-content:center;padding:24px;background:rgba(15,23,42,.58);backdrop-filter:blur(2px)}" +
        ".pgs-processing-overlay.is-visible{display:flex}" +
        ".pgs-processing-dialog{width:min(420px,100%);box-sizing:border-box;padding:32px 24px;text-align:center;background:#fff;border-radius:16px;box-shadow:0 24px 70px rgba(0,0,0,.28);color:#1f2937}" +
        ".pgs-processing-spinner{width:44px;height:44px;margin:0 auto 18px;border:5px solid #e5e7eb;border-top-color:#2563eb;border-radius:50%;animation:pgs-processing-spin .8s linear infinite}" +
        ".pgs-processing-title{margin:0 0 8px;font-size:20px;font-weight:700;line-height:1.5}" +
        ".pgs-processing-message{margin:0;color:#4b5563;font-size:14px;line-height:1.7}" +
        "@keyframes pgs-processing-spin{to{transform:rotate(360deg)}}" +
        "@media (prefers-reduced-motion:reduce){.pgs-processing-spinner{animation-duration:1.6s}}";
      document.head.appendChild(style);

      var overlay = document.createElement("div");
      overlay.className = "pgs-processing-overlay";
      overlay.setAttribute("role", "dialog");
      overlay.setAttribute("aria-modal", "true");
      overlay.setAttribute("aria-labelledby", "pgsProcessingTitle");
      overlay.setAttribute("aria-describedby", "pgsProcessingMessage");
      overlay.setAttribute("aria-hidden", "true");
      overlay.tabIndex = -1;
      overlay.innerHTML =
        '<div class="pgs-processing-dialog">' +
          '<div class="pgs-processing-spinner" aria-hidden="true"></div>' +
          '<p class="pgs-processing-title" id="pgsProcessingTitle">' + PREPARING_DEFAULT_TITLE + '</p>' +
          '<p class="pgs-processing-message" id="pgsProcessingMessage">' + PREPARING_DEFAULT_MESSAGE + '</p>' +
        "</div>";
      document.body.appendChild(overlay);
      previewPreparingUi = {
        overlay: overlay,
        titleEl: overlay.querySelector("#pgsProcessingTitle"),
        messageEl: overlay.querySelector("#pgsProcessingMessage"),
        inerted: [],
        previousFocus: null,
        previousOverflow: ""
      };
    }

    var ui = previewPreparingUi;
    if (isPreparing) {
      /* 文言は表示中でも差し替えられるようにする
         （切り抜き保存 → プレビュー生成、とモーダルを出したまま進むため） */
      if (ui.titleEl) ui.titleEl.textContent = title || PREPARING_DEFAULT_TITLE;
      if (ui.messageEl) ui.messageEl.textContent = message || PREPARING_DEFAULT_MESSAGE;
      if (ui.overlay.classList.contains("is-visible")) return;
      ui.previousFocus = document.activeElement;
      ui.previousOverflow = document.body.style.overflow;
      ui.inerted = [];
      Array.prototype.forEach.call(document.body.children, function (el) {
        if (el !== ui.overlay && !el.inert) {
          el.inert = true;
          ui.inerted.push(el);
        }
      });
      document.body.style.overflow = "hidden";
      ui.overlay.classList.add("is-visible");
      ui.overlay.setAttribute("aria-hidden", "false");
      ui.overlay.focus();
      return;
    }

    ui.overlay.classList.remove("is-visible");
    ui.overlay.setAttribute("aria-hidden", "true");
    ui.inerted.forEach(function (el) { el.inert = false; });
    ui.inerted = [];
    document.body.style.overflow = ui.previousOverflow;
    if (ui.previousFocus && document.documentElement.contains(ui.previousFocus)) {
      ui.previousFocus.focus();
    }
    ui.previousFocus = null;
  }

  /* ---------- 全スロットへ画像反映 ---------- */
  function applyImage(src) {
    $all("img[data-slot]").forEach(function (img) {
      if (img.dataset.warp === "mug") {
        applyMugWarp(img, src);
      } else {
        img.src = src;
      }
    });
    /* 丸型キーホルダー: CSS（object-fit:cover + border-radius:50%）で
       中央の円形切り抜きとして表示される。実ファイルの生成は購入時に行う */
    $all("img[data-slot-round]").forEach(function (img) { img.src = src; });
    $all("img[data-thumb]").forEach(function (img) { img.src = src; });
  }

  /* ---------- マグカップ用 canvas ワープ ----------
     元テーマ snippets/product-mugcup.liquid の実装を移植。
     画像を二次ベジェ曲線に沿って縦方向へずらし、カップの丸みを再現する。 */
  function getQuadraticBezierXYatT(startPt, controlPt, endPt, T) {
    var x = Math.pow(1 - T, 2) * startPt.x +
            2 * (1 - T) * T * controlPt.x +
            Math.pow(T, 2) * endPt.x;
    var y = Math.pow(1 - T, 2) * startPt.y +
            2 * (1 - T) * T * controlPt.y +
            Math.pow(T, 2) * endPt.y;
    return { x: x, y: y };
  }

  function warpHorizontally(imageToWarp, invertCurve) {
    var w = imageToWarp.width;
    var h = imageToWarp.height;

    /* アスペクト比で warp_percentage を決定し、マグの丸みに合わせてカーブを深める */
    var aspectRatio = w / h;
    var warpPercentage = 0.18 * aspectRatio;
    warpPercentage = Math.max(0.04, Math.min(0.18, warpPercentage));
    var warpYOffset = warpPercentage * h;

    var canvas = document.createElement("canvas");
    var ctx = canvas.getContext("2d");
    canvas.width = w;
    canvas.height = h + Math.ceil(warpYOffset * 2);

    var startPoint = { x: 0, y: 0 };
    var controlPoint = { x: w / 2, y: invertCurve ? warpYOffset : -warpYOffset };
    var endPoint = { x: w, y: 0 };

    var offsets = [];
    for (var t = 0; t < w; t++) {
      offsets.push(parseInt(getQuadraticBezierXYatT(startPoint, controlPoint, endPoint, t / w).y, 10));
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (var x = 0; x < w; x++) {
      ctx.drawImage(imageToWarp, x, 0, 1, h, x, warpYOffset + offsets[x], 1, h);
    }
    return canvas;
  }

  function applyMugWarp(imgEl, src) {
    imgEl.dataset.src = src; /* 本来の画像URLを保持 */
    var probe = new Image();
    probe.crossOrigin = "anonymous"; /* CORS対策（元実装どおり） */
    probe.onload = function () {
      if (imgEl.dataset.src !== src) return; /* 選び直し前の遅い onload は破棄 */
      try {
        var canvas = warpHorizontally(probe, true);
        imgEl.src = canvas.toDataURL();
      } catch (e) {
        /* CORS で canvas が汚染された場合などは、ワープなしで表示 */
        console.warn("mug warp fallback:", e);
        imgEl.src = src;
      }
    };
    probe.onerror = function () {
      if (imgEl.dataset.src === src) imgEl.src = src;
    };
    probe.src = src;
  }

  /* ---------- OLD:解像度チェック（印刷品質の目安） 
  function checkResolution(img) {
    var note = $("#dpiNote");
    if (!note) return;
    var minSide = Math.min(img.naturalWidth, img.naturalHeight);
    note.classList.remove("dpi-ok", "dpi-warn");
    if (minSide >= 1000) {
      note.classList.add("dpi-ok", "show");
      note.innerHTML =
        "<span aria-hidden='true'>✔</span><span><b>印刷に十分な解像度です</b>（" +
        img.naturalWidth + " × " + img.naturalHeight + " px）。このままきれいにプリントできます。</span>";
    } else {
      note.classList.add("dpi-warn", "show");
      note.innerHTML =
        "<span aria-hidden='true'>！</span><span><b>解像度がやや低めです</b>（" +
        img.naturalWidth + " × " + img.naturalHeight + " px）。小さめのグッズ（ステッカー・キーホルダー）がおすすめです。" +
        "大きくプリントすると粗さが目立つ場合があります。</span>";
    }
  }---------- */

  /* ---------- 商品の印刷設定（mm単位） ---------- */
  const PRODUCT_PRINT_SETTINGS = [
    { name: 'Tシャツ（全面）', maxWidthInput: 280, maxHeightInput: 280 },
    { name: 'Tシャツ（ワンポイント）', maxWidthInput: 11, maxHeightInput: 11 },
    { name: 'アクリルキーホルダー50mm', maxWidthInput: 50, maxHeightInput: 50 },
    { name: 'ホワイトステッカー50mm', maxWidthInput: 50, maxHeightInput: 50 },
    { name: 'マグカップ', maxWidthInput: 94, maxHeightInput: 89 },
  ];

  /* ---------- 解像度チェック（印刷品質の目安） ---------- */
  function checkResolution(img) {
    var note = $("#dpiNote");
    if (!note) return;

    note.classList.remove("dpi-ok", "dpi-warn");

    // 300dpi未満（解像度不足）の商品名を格納する配列
    var lowDpiProducts = [];

    PRODUCT_PRINT_SETTINGS.forEach(function(setting) {
      // 1. アスペクト比を維持して枠に収めたときのスケール（mm/pixel）を算出
      var scale = Math.min(setting.maxWidthInput / img.naturalWidth, setting.maxHeightInput / img.naturalHeight);
      
      // 2. そのスケールでのDPIを計算 (1インチ ＝ 25.4mm)
      var dpi = 25.4 / scale;

      // 300dpi未満の場合は、配列に商品名を追加
      if (dpi < 300) {
        lowDpiProducts.push(setting.name);
      }
    });

    if (lowDpiProducts.length === 0) {
      // すべての商品で300dpi以上をクリアした場合
      note.classList.add("dpi-ok", "show");
      note.innerHTML =
        "<span aria-hidden='true'>✔</span><span><b>印刷に十分な解像度です</b>（" +
        img.naturalWidth + " × " + img.naturalHeight + " px）。このままきれいにプリントできます。</span>";
    } else {
      // 1つでも300dpi未満の商品がある場合
      note.classList.add("dpi-warn", "show");

      // 対象の商品リスト（HTML）を作成
      var productListHtml = "<div><ul style='margin: 8px 0 0 20px; padding: 0;'>";
      lowDpiProducts.forEach(function(name) {
        productListHtml += "<li>" + name + "</li>";
      });
      productListHtml += "</ul></div>";

      note.innerHTML =
        "<p><span aria-hidden='true'>！</span><span><b>以下の商品を作るには解像度がやや低めです</b>（" +
        img.naturalWidth + " × " + img.naturalHeight + " px）。プリントすると粗さが目立つ場合があります。</span></p>" +
        productListHtml;
    }
  }

  /* ---------- アップロード中のブロッキングオーバーレイ ----------
     アップロード完了（またはエラー）までクリック・タブ移動を含む
     他の操作をできないようにする。#upStatus チップとは別に、
     画面全体を覆うモーダルとして表示する。 */
  var uploadOverlayEl = null;
  var uploadOverlayPrevFocus = null;
  function ensureUploadOverlay() {
    if (uploadOverlayEl) return uploadOverlayEl;
    var overlay = document.createElement("div");
    overlay.className = "pgs-upload-overlay";
    overlay.id = "pgsUploadOverlay";
    overlay.setAttribute("role", "alertdialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-live", "assertive");
    overlay.setAttribute("aria-label", "画像をアップロード中");
    overlay.tabIndex = -1;
    overlay.innerHTML =
      '<div class="pgs-upload-card">' +
        '<div class="pgs-upload-spinner" aria-hidden="true"></div>' +
        '<p class="pgs-upload-title">画像をアップロードしています…</p>' +
        '<p class="pgs-upload-sub">完了までそのままお待ちください</p>' +
      '</div>';
    document.body.appendChild(overlay);
    uploadOverlayEl = overlay;
    return overlay;
  }
  function trapOverlayKeydown(e) {
    /* Tab でオーバーレイの外へフォーカスが逃げるのを防ぐ（フォーカス可能な要素が
       オーバーレイ内に無いため、Tab 自体を無効化して現在のフォーカスを維持する） */
    if (e.key === "Tab" || e.key === "Escape") e.preventDefault();
  }
  function showUploadOverlay() {
    var overlay = ensureUploadOverlay();
    uploadOverlayPrevFocus = document.activeElement;
    overlay.classList.add("show");
    document.body.classList.add("pgs-noscroll");
    document.addEventListener("keydown", trapOverlayKeydown, true);
    overlay.focus();
  }
  function hideUploadOverlay() {
    if (!uploadOverlayEl) return;
    uploadOverlayEl.classList.remove("show");
    document.body.classList.remove("pgs-noscroll");
    document.removeEventListener("keydown", trapOverlayKeydown, true);
    if (uploadOverlayPrevFocus && typeof uploadOverlayPrevFocus.focus === "function") {
      uploadOverlayPrevFocus.focus();
    }
    uploadOverlayPrevFocus = null;
  }

  /* ---------- アップロード状態チップ ---------- */
  function setUploadStatus(kind, msg) {
    var el = $("#upStatus");
    if (!el) return;
    if (!kind) { el.className = "up-status"; el.innerHTML = ""; return; }
    el.className = "up-status show is-" + kind;
    el.innerHTML =
      (kind === "uploading" ? "<span class='spinner' aria-hidden='true'></span>" :
       kind === "done" ? "<span aria-hidden='true'>✔</span>" : "<span aria-hidden='true'>！</span>") +
      "<span></span>";
    el.querySelector("span:last-child").textContent = msg;
  }

  /* ?image= の復元中表示は、通常のアップロード状況とは分けて
     商品ページ上部の #uploadSticky 内だけに表示する。 */
  var queryRestoreStatusEl = null;
  function setQueryRestoreStatus(isRestoring, keepSticky) {
    var sticky = $("#uploadSticky");
    if (!sticky) return;

    if (isRestoring) {
      sticky.classList.add("show");
      sticky.classList.add("is-restoring");
      if (!queryRestoreStatusEl) {
        var wrap = sticky.querySelector(".wrap") || sticky;
        queryRestoreStatusEl = document.createElement("div");
        queryRestoreStatusEl.className = "pgs-query-restore-status";
        queryRestoreStatusEl.setAttribute("role", "status");
        queryRestoreStatusEl.setAttribute("aria-live", "polite");
        queryRestoreStatusEl.innerHTML =
          "<span class='spinner' aria-hidden='true'></span>" +
          "<span>アップロード画像を反映中…</span>";
        var changeButton = wrap.querySelector("[data-open-file]");
        wrap.insertBefore(queryRestoreStatusEl, changeButton || null);
      }
      return;
    }

    if (queryRestoreStatusEl) {
      queryRestoreStatusEl.remove();
      queryRestoreStatusEl = null;
    }
    sticky.classList.remove("is-restoring");
    if (!keepSticky) sticky.classList.remove("show");
  }

  /* ---------- 購入可否（画像 + 販売元の検証） ---------- */
  function sellerOk() {
    if (!state.client) return false;
    if (!Array.isArray(CFG.sellers)) return false;
    return CFG.sellers.indexOf(state.client) !== -1;
  }
  function updateBuyable() {
    var canBuyGlobal = !!state.hostedUrl && !state.uploading && sellerOk();
    /* 丸型: エディタで調整済み(roundUrl) または ベース画像あり（購入時に中央切り抜きを自動生成） */
    var canBuyRound = (!!state.roundUrl || !!state.hostedUrl) && !state.uploading && sellerOk();
    $all(".addToCartBtn").forEach(function (btn) {
      var card = btn.closest("[data-product-card]");
      var isRound = card && card.dataset.imageSource === "round";
      btn.disabled = isRound ? !canBuyRound : !canBuyGlobal;
      if (state.uploading) {
        btn.dataset.holdLabel = btn.dataset.holdLabel || btn.textContent;
        btn.textContent = "画像アップロード中…";
      } else if (btn.dataset.holdLabel) {
        btn.textContent = btn.dataset.holdLabel;
        delete btn.dataset.holdLabel;
      }
    });
    var note = $("#availabilityNote");
    if (note) {
      if (state.client && !sellerOk()) {
        note.textContent = "この商品は買えません。対応しているサイトからご購入ください。";
        note.classList.add("show");
      } else {
        note.classList.remove("show");
      }
    }
  }

  /* ---------- クエリ ↔ 状態の同期 ---------- */
  function syncQuery() {
    if (!window.history || !history.replaceState) return;
    var p = new URLSearchParams(window.location.search);
    if (state.hostedUrl) p.set("image", state.hostedUrl); else p.delete("image");
    if (state.client) p.set("client", state.client); else p.delete("client");
    var q = p.toString();
    history.replaceState(null, "", window.location.pathname + (q ? "?" + q : "") + window.location.hash);
    decorateCarryLinks();
  }
  /* ナビ・CTA リンクに現在の image/client を引き継ぐ（#アンカーは保持） */
  function decorateCarryLinks() {
    if (!state.hostedUrl) return;
    var q = "image=" + encodeURIComponent(state.hostedUrl) + "&client=" + encodeURIComponent(state.client);
    $all("a[data-carry-params]").forEach(function (a) {
      var href = a.getAttribute("href") || "";
      var hashIdx = href.indexOf("#");
      var hash = hashIdx >= 0 ? href.slice(hashIdx) : "";
      var base = (hashIdx >= 0 ? href.slice(0, hashIdx) : href).split("?")[0];
      a.href = base + "?" + q + hash;
    });
  }

  /* ============================================================
     通信レイヤー（タイムアウト＋再試行）
     ------------------------------------------------------------
     アップロード先は Cloudflare Workers（R2）、旧 Drive 画像の読み出しのみ
     GAS プロキシ。どちらも下記の共通ルール（タイムアウト・再試行・
     失敗理由の分類）に載せる。
     ------------------------------------------------------------
     従来は fetch を一発で呼ぶだけだったため、
       ・回線が輻輳して応答待ちの間にコネクションが切られた
       ・応答が返らないまま無限に待ち続けた
     といった一時的な障害がそのまま利用者向けエラーになっていた。
     （GAS 側は完走して Drive に保存済みでも、ブラウザ側は失敗と判定される）

     ここでは全てのサーバ呼び出しを retryRequest() に集約し、
       ・AbortController によるタイムアウト（処理内容ごとに設定）
       ・指数バックオフ＋ジッターによる自動再試行
       ・失敗理由の分類（network / timeout / format / app / config）
     を共通で行う。app（GAS が ok:false を返した業務エラー）と
     config（エンドポイント未設定）は再試行しても結果が変わらないため対象外。
     ============================================================ */

  /* 失敗理由を pgsKind に持たせた Error を作る（再試行可否の判定に使う） */
  function pgsError(message, kind) {
    var err = new Error(message);
    err.pgsKind = kind;
    return err;
  }

  var MSG_NETWORK = "通信が不安定なため、サーバーからの応答を受け取れませんでした。通信環境をご確認のうえ、もう一度お試しください";
  var MSG_FORMAT = "サーバーから想定外の応答が返りました。時間をおいて、もう一度お試しください";

  /* GAS へ1回だけ POST する。
     ・Content-Type を指定しない（text/plain）ことで CORS プリフライトを回避
     ・timeoutMs を過ぎたら AbortController で中断する */
  function gasFetchOnce(payload, timeoutMs) {
    if (!CFG.gasEndpoint) {
      return Promise.reject(pgsError("GASエンドポイントが未設定です（snippets/pgs-config.liquid を確認）", "config"));
    }

    var controller = null;
    try { controller = (typeof AbortController !== "undefined") ? new AbortController() : null; } catch (e) { controller = null; }

    var timedOut = false;
    var timer = null;
    if (timeoutMs > 0 && controller) {
      timer = setTimeout(function () {
        timedOut = true;
        try { controller.abort(); } catch (e) {}
      }, timeoutMs);
    }

    var opts = {
      method: "POST",
      /* headers 指定なし（text/plain）= simple request でプリフライト不要 */
      body: JSON.stringify(payload)
    };
    if (controller) opts.signal = controller.signal;

    var request = fetch(CFG.gasEndpoint, opts).then(function (res) {
      return res.text().then(function (text) { return { status: res.status, text: text }; });
    });

    return request.then(function (raw) {
      if (timer) clearTimeout(timer);
      var json = null;
      try { json = JSON.parse(raw.text); } catch (e) { /* ログイン画面等のHTML */ }
      if (!json) {
        throw pgsError(MSG_FORMAT + "（HTTP " + raw.status + "）", "format");
      }
      if (!json.ok) {
        throw pgsError("GAS側エラー: " + (json.error || "不明なエラー"), "app");
      }
      return json;
    }, function (err) {
      /* fetch 自体の失敗（ネットワーク到達不可・CORSブロック・中断） */
      if (timer) clearTimeout(timer);
      if (timedOut) {
        throw pgsError("応答が時間内に返りませんでした（" + Math.max(1, Math.round(timeoutMs / 1000)) + "秒）。通信環境をご確認のうえ、もう一度お試しください", "timeout");
      }
      if (err && err.pgsKind) throw err;
      throw pgsError(MSG_NETWORK, "network");
    });
  }

  /* 1回分の処理（runOnce）を指数バックオフ＋ジッターで再試行する共通ラッパー。
     opts: { attempts, retryDelayMs } / label はデバッグ出力用の名前 */
  function retryRequest(runOnce, opts, label) {
    opts = opts || {};
    var attempts = Math.max(1, opts.attempts || 1);
    var retryDelayMs = opts.retryDelayMs || 800;

    function run(attempt) {
      return runOnce().catch(function (err) {
        var kind = err && err.pgsKind;
        /* config（設定不備）と app（サーバ側の業務エラー）は再試行しても変わらない */
        var retriable = (kind === "network" || kind === "timeout" || kind === "format");
        if (!retriable || attempt >= attempts) throw err;
        if (PGS_DEBUG) {
          /* eslint-disable-next-line no-console */
          console.warn("[PGS] 再試行 " + attempt + "/" + attempts + " (" + (label || "request") + "): " + (err && err.message));
        }
        var wait = retryDelayMs * Math.pow(2, attempt - 1) + Math.floor(Math.random() * 300);
        return new Promise(function (resolve) { setTimeout(resolve, wait); })
          .then(function () { return run(attempt + 1); });
      });
    }

    return run(1);
  }

  /* gasFetchOnce を再試行するラッパー。opts: { attempts, timeoutMs, retryDelayMs } */
  function gasRequest(payload, opts) {
    opts = opts || {};
    return retryRequest(function () {
      return gasFetchOnce(payload, opts.timeoutMs || 0);
    }, opts, payload.action);
  }

  /* ============================================================
     Cloudflare Workers へのアップロード（1回分）
     ------------------------------------------------------------
     参照実装（_reference/cloudflare_upload_test.html）と同じ契約:
       POST multipart/form-data { file: <Blob|File> }
       → { success: true, url: "<R2の公開URL>" }
     multipart/form-data は CORS セーフリストの Content-Type なので
     プリフライトは発生しない（Content-Type は明示せずブラウザに任せる。
     自前で指定すると boundary が付かず Worker 側で解析できなくなる）。
     再試行のたびに FormData を作り直すため、生成関数を受け取る。
     ============================================================ */
  function workerFetchOnce(buildForm, timeoutMs) {
    if (!WORKER_ENDPOINT) {
      return Promise.reject(pgsError("アップロード先が未設定です（assets/pgs.js の DEFAULT_WORKER_ENDPOINT を確認）", "config"));
    }

    var controller = null;
    try { controller = (typeof AbortController !== "undefined") ? new AbortController() : null; } catch (e) { controller = null; }

    var timedOut = false;
    var timer = null;
    if (timeoutMs > 0 && controller) {
      timer = setTimeout(function () {
        timedOut = true;
        try { controller.abort(); } catch (e) {}
      }, timeoutMs);
    }

    var opts = { method: "POST", body: buildForm() };
    if (controller) opts.signal = controller.signal;

    var request = fetch(WORKER_ENDPOINT, opts).then(function (res) {
      return res.text().then(function (text) { return { status: res.status, text: text }; });
    });

    return request.then(function (raw) {
      if (timer) clearTimeout(timer);
      var json = null;
      try { json = JSON.parse(raw.text); } catch (e) { /* エラーページ等のHTML */ }
      if (!json) {
        throw pgsError(MSG_FORMAT + "（HTTP " + raw.status + "）", "format");
      }
      if (!json.success) {
        throw pgsError("アップロードに失敗しました: " + (json.error || "不明なエラー"), "app");
      }
      if (!json.url) {
        throw pgsError("アップロードに失敗しました: 保存先URLを取得できませんでした", "app");
      }
      return json;
    }, function (err) {
      /* fetch 自体の失敗（ネットワーク到達不可・CORSブロック・中断） */
      if (timer) clearTimeout(timer);
      if (timedOut) {
        throw pgsError("応答が時間内に返りませんでした（" + Math.max(1, Math.round(timeoutMs / 1000)) + "秒）。通信環境をご確認のうえ、もう一度お試しください", "timeout");
      }
      if (err && err.pgsKind) throw err;
      throw pgsError(MSG_NETWORK, "network");
    });
  }

  /* ---------- R2 へのアップロード（共通） ----------
     ・Worker が multipart/form-data の "file" を受け取り R2 へ保存、公開URLを返す
     ・File / Blob をそのまま送るため、base64 化（＋約33%の肥大化）は発生しない
     ・タイムアウトは送信サイズに応じて 60〜300 秒の範囲で自動決定する
       （20MB の原本を最悪 25KB/秒 で送り切る想定。短いタイムアウトは
         輻輳した回線で「サーバ側は保存済みなのに失敗表示」を招くため逆効果）

     ■ 再試行についての注意
       アップロードはべき等ではない（Worker 側がキーを都度発行するため）ので、
       「R2 への保存は完了したがブラウザが応答を受け取れなかった」ケースで
       再試行すると同じ画像が複数保存される。カートに載るのは最後に成功した
       1件だけなので実害は無いが、重複を一切許容できない場合は Liquid 側の
       window.PGS に uploadRetryAttempts: 1 を出力すれば再試行を無効化できる。
  ---------------------------------- */
  var UPLOAD_ATTEMPTS = Math.max(1, Number(CFG.uploadRetryAttempts) || 2);

  function uploadTimeoutMs(byteLength) {
    /* おおよそ 25KB/秒 の実効上り速度を最悪値として見積もる */
    var kb = Math.round(Number(byteLength || 0) / 1024);
    return Math.min(300000, Math.max(60000, Math.round(kb / 25) * 1000));
  }

  /* Blob / File をそのまま Worker へ送る。戻り値は { success, url, ... } */
  function uploadBlob(blob, filename, debugLabel) {
    var name = filename || "image";
    var label = debugLabel || "upload";
    return retryRequest(function () {
      return workerFetchOnce(function () {
        var form = new FormData();
        /* 第3引数のファイル名が R2 側の保存名・拡張子の判定に使われる */
        form.append("file", blob, name);
        /* Worker 側が未対応なら無視されるだけの補助情報（販売元の記録用） */
        if (state.client) form.append("client", state.client);
        return form;
      }, uploadTimeoutMs(blob && blob.size));
    }, { attempts: UPLOAD_ATTEMPTS, retryDelayMs: 1200 }, label)
      .then(function (json) {
        markServerTiming(label, json.timing);
        return json;
      });
  }

  /* canvas 由来の dataURL をアップロードする（丸型切り抜き・モックプレビュー） */
  function uploadDataUrl(dataUrl, filename, debugLabel) {
    var blob;
    try {
      blob = dataUrlToBlob(dataUrl);
    } catch (e) {
      return Promise.reject(pgsError("画像データを変換できませんでした", "app"));
    }
    return uploadBlob(blob, filename, debugLabel);
  }

  /* 丸型エディタ（pgs-round-editor.js）からも同じ経路で保存させる */
  window.PGSUpload = { blob: uploadBlob, dataUrl: uploadDataUrl, endpoint: WORKER_ENDPOINT };

  function uploadToWorker(file, revision, onDone) {
    if (!WORKER_ENDPOINT) {
      setUploadStatus("error", "アップロード先が未設定です。テーマ設定をご確認ください。");
      onDone(false);
      return;
    }
    state.uploading = true;
    updateBuyable();
    setUploadStatus("uploading", "画像をアップロードしています…（" + file.name + "）");
    showUploadOverlay();

    /* 選択された File をそのまま送る（画面プレビュー用の dataURL とは別経路）。
       従来は FileReader の結果を base64 にして JSON へ載せていた。 */
    uploadBlob(file, file.name, "原画像アップロード")
      .then(function (json) {
        if (revision !== imageRevision) return; /* 旧画像のアップロード完了は反映しない */
        state.hostedUrl = normalizeHostedImageUrl(json.url);
        state.uploading = false;
        setUploadStatus("done", "アップロード完了。全商品をご購入いただけます。");
        syncQuery();
        updateBuyable();
        hideUploadOverlay();
        /* 1-D: 「カートに追加」時に初回ロードで待たされないよう、
           空き時間のうちに html2canvas を先読みしておく（失敗しても無視） */
        prefetchHtml2Canvas();
        onDone(true);
      })
      .catch(function (err) {
        console.error("R2 upload error:", err);
        if (revision !== imageRevision) return; /* 最新画像の状態・UIを壊さない */
        state.uploading = false;
        state.hostedUrl = "";
        setUploadStatus("error", "アップロードに失敗しました。" + (err && err.message ? err.message : "通信環境をご確認のうえ、もう一度お試しください"));
        updateBuyable();
        hideUploadOverlay();
        onDone(false);
      });
  }

  /* ============================================================
     セッション内画像キャッシュ（sessionStorage）
     ------------------------------------------------------------
     これまで取得結果を保持していた remoteCache はページ単位のメモリ上の
     オブジェクトだったため、ページを遷移するたびに同じ画像を GAS から
     取り直していた。1回の往復が重い（＝失敗しやすい）処理なので、
     縮小版の dataURL はタブ内で共有できる sessionStorage にも保存し、
     遷移後は GAS を叩かずに即復元できるようにする。

     ・タブを閉じれば消える（sessionStorage の性質）ため、共有端末でも
       画像が残り続けることはない。
     ・原本（size 未指定）は最大 27MB になり得るため保存対象外。
     ・容量超過時は自前のインデックスで古いものから捨て、
       それでも入らなければ諦める（キャッシュは無くても動作する）。
     ============================================================ */
  var SESSION_CACHE_PREFIX = "pgs:img:";
  var SESSION_CACHE_INDEX = "pgs:img:__index";
  var SESSION_CACHE_MAX_CHARS = 1500000; /* 1エントリの上限（約1.5MB） */
  var SESSION_CACHE_MAX_ENTRIES = 4;

  function sessionStore() {
    try { return window.sessionStorage || null; } catch (e) { return null; } /* プライベートモード等 */
  }
  function cacheIndexRead(ss) {
    try {
      var list = JSON.parse(ss.getItem(SESSION_CACHE_INDEX) || "[]");
      return Object.prototype.toString.call(list) === "[object Array]" ? list : [];
    } catch (e) { return []; }
  }
  function cacheIndexWrite(ss, list) {
    try { ss.setItem(SESSION_CACHE_INDEX, JSON.stringify(list)); } catch (e) {}
  }
  function sessionCacheClear() {
    var ss = sessionStore();
    if (!ss) return;
    cacheIndexRead(ss).forEach(function (k) { try { ss.removeItem(k); } catch (e) {} });
    try { ss.removeItem(SESSION_CACHE_INDEX); } catch (e) {}
  }
  function sessionCacheGet(key) {
    var ss = sessionStore();
    if (!ss) return null;
    try { return ss.getItem(SESSION_CACHE_PREFIX + key) || null; } catch (e) { return null; }
  }
  function sessionCacheSet(key, dataUrl) {
    var ss = sessionStore();
    if (!ss || !dataUrl || dataUrl.length > SESSION_CACHE_MAX_CHARS) return;

    var full = SESSION_CACHE_PREFIX + key;
    var list = cacheIndexRead(ss).filter(function (k) { return k !== full; });
    while (list.length >= SESSION_CACHE_MAX_ENTRIES) {
      var oldest = list.shift();
      try { ss.removeItem(oldest); } catch (e) {}
    }

    try {
      ss.setItem(full, dataUrl);
    } catch (e) {
      /* QuotaExceededError。一旦すべて捨ててから1度だけ再試行する */
      sessionCacheClear();
      list = [];
      try { ss.setItem(full, dataUrl); } catch (e2) { return; }
    }

    list.push(full);
    cacheIndexWrite(ss, list);
  }

  /* ---------- リモート画像を dataURL で取得 ----------
     canvas 加工（丸型切り抜き・マグのワープ・モック撮影）には CORS クリーンな
     ピクセルが必要なため、加工の直前にここで dataURL 化する。取得経路は2つ:

       ・Cloudflare R2 の公開URL … fetch → Blob → dataURL（通常の経路）
         CORS が許可されていない場合は URL のまま返す。表示は <img src> で
         成立し、canvas 側にも taint 時のフォールバックがあるため止めない。
       ・旧 Drive の直URL … drive.google.com は CORS ヘッダを返さないため、
         従来どおり GAS（action:'fetch'）経由で base64 取得する。

     キャッシュは2段構え（メモリ → sessionStorage → 取得）。
     エディタ（pgs-round-editor.js）からも window.PGSRemote 経由で利用する。 */
  var REMOTE_FETCH_ATTEMPTS = 3;
  var remoteCache = {};
  /* R2 の公開URLなど、ブラウザから直接読める画像を dataURL 化する。
     size は Drive の thumbnailLink 用の引数なのでここでは使わない
     （R2 は原本をそのまま配信し、縮小版のエンドポイントを持たないため）。 */
  function fetchPublicAsDataUrl(url) {
    var cacheKey = "url:" + url;
    if (remoteCache[cacheKey]) return remoteCache[cacheKey];

    var stored = sessionCacheGet(cacheKey);
    if (stored) {
      remoteCache[cacheKey] = Promise.resolve(stored);
      return remoteCache[cacheKey];
    }

    remoteCache[cacheKey] = fetch(url, { mode: "cors", credentials: "omit" })
      .then(function (res) {
        if (!res.ok) throw pgsError("画像を取得できませんでした（HTTP " + res.status + "）", "app");
        return res.blob();
      })
      .then(blobToDataUrl)
      .then(function (dataUrl) {
        sessionCacheSet(cacheKey, dataUrl);
        return dataUrl;
      })
      .catch(function (err) {
        /* CORS 不許可・ネットワーク断など。表示だけなら URL のままで成立するため、
           ここでは失敗させずに元の URL を返す（canvas 側に taint 時の退避がある）。 */
        console.warn("public image fetch fallback:", err);
        return url;
      });
    return remoteCache[cacheKey];
  }

  function fetchRemoteAsDataUrl(url, size) {
    if (/^data:/.test(url)) return Promise.resolve(url);
    var driveId = driveFileIdFromUrl(url);
    if (!driveId) {
      /* R2 の公開URL（現行のアップロード先）や同一オリジンの画像 */
      return fetchPublicAsDataUrl(url);
    }
    /* キャッシュは size ごとに分ける（縮小版と原本を取り違えないため） */
    var cacheKey = driveId + ":" + (size || "orig");
    if (remoteCache[cacheKey]) return remoteCache[cacheKey];

    /* 縮小版のみ sessionStorage の対象。原本は巨大なので保存しない */
    var cacheable = !!size;
    if (cacheable) {
      var stored = sessionCacheGet(cacheKey);
      if (stored) {
        remoteCache[cacheKey] = Promise.resolve(stored);
        return remoteCache[cacheKey];
      }
    }

    /* size を省略した場合は原本をそのまま取得する。丸型トリミング（エディタでの
       編集・購入時の中央自動切り抜き）は印刷用の実データを生成するため、必ず
       原本の解像度を使う。size を指定するのは、参考画像にすぎないモック合成
       プレビューの高速化用途のみ（呼び出し側で明示的に渡す）。 */
    var body = { action: "fetch", id: driveId };
    if (size) body.size = size;

    remoteCache[cacheKey] = gasRequest(body, {
      attempts: REMOTE_FETCH_ATTEMPTS,
      /* 原本フォールバック時は GAS 側で20MB超の base64 化が走るため長めに取る */
      timeoutMs: size ? 60000 : 180000,
      retryDelayMs: 700
    }).then(function (json) {
      if (!json.data) throw pgsError("画像を取得できませんでした", "app");
      var dataUrl = "data:" + (json.mimeType || "image/png") + ";base64," + json.data;
      if (cacheable) sessionCacheSet(cacheKey, dataUrl);
      return dataUrl;
    });
    remoteCache[cacheKey].catch(function () { delete remoteCache[cacheKey]; }); /* 失敗はキャッシュしない */
    return remoteCache[cacheKey];
  }
  /* エディタ（pgs-round-editor.js）には常に原本解像度を渡す（size 指定なし） */
  window.PGSRemote = { asDataUrl: function (url) { return fetchRemoteAsDataUrl(url); } };

  /* Driveの表示用画像は長辺1600pxなどに縮小されることがあるため、
     解像度判定に使う原画像の寸法はGASから取得する。 */
  function fetchOriginalDimensions(url) {
    var driveId = driveFileIdFromUrl(url);
    if (!driveId || !CFG.gasEndpoint) return Promise.resolve(null);

    function request(attempt) {
      /* 通信レベルの一時障害は gasRequest 側で吸収する。
         ここでの再試行は「アップロード直後で Drive の画像メタデータが
         まだ生成されていない」ケースを待つためのもの。 */
      return gasRequest({ action: "metadata", id: driveId }, {
        attempts: 2,
        timeoutMs: 20000,
        retryDelayMs: 600
      }).then(function (json) {
        var width = Number(json.width);
        var height = Number(json.height);
        if (width && height) return { naturalWidth: width, naturalHeight: height };
        return null;
      }).catch(function () {
        return null;
      }).then(function (dimensions) {
        if (dimensions) return dimensions;
        if (attempt >= 2) return null;
        return new Promise(function (resolve) {
          setTimeout(resolve, 500 * (attempt + 1));
        }).then(function () { return request(attempt + 1); });
      });
    }

    return request(0);
  }

  /* ---------- 丸型: 中央の円形切り抜きPNGを生成 ----------
     プレビューは CSS で円形表示しているだけなので、
     購入時にここで実ファイル（1024px 透過PNG）を作る。
     リモート画像（?image= クエリからの復元）は GAS プロキシ経由で取得し、
     canvas の CORS 汚染を回避する。 */
  function centerCropPng(src) {
    return fetchRemoteAsDataUrl(src).then(function (usableSrc) {
      return new Promise(function (resolve, reject) {
        var im = new Image();
        if (!/^data:/.test(usableSrc)) im.crossOrigin = "anonymous";
        im.onload = function () {
          var s = Math.min(im.naturalWidth, im.naturalHeight);
          var sx = (im.naturalWidth - s) / 2;
          var sy = (im.naturalHeight - s) / 2;
          var c = document.createElement("canvas");
          c.width = c.height = 1024;
          var cx = c.getContext("2d");
          cx.beginPath();
          cx.arc(512, 512, 512, 0, Math.PI * 2);
          cx.closePath();
          cx.clip();
          cx.drawImage(im, sx, sy, s, s, 0, 0, 1024, 1024);
          try {
            resolve(c.toDataURL("image/png"));
          } catch (e) {
            reject(e); /* CORS で canvas が汚染された場合 */
          }
        };
        im.onerror = function () { reject(new Error("画像を読み込めませんでした")); };
        im.src = usableSrc;
      });
    });
  }

  /* 丸型商品のカート追加時に使う切り抜き画像URLを保証する。
     エディタで調整済みならその URL、未調整なら中央切り抜きを生成して保存 */
  function ensureRoundUrl() {
    if (state.roundUrl) return Promise.resolve(state.roundUrl);
    var baseName = (state.fileName || "image").replace(/\.[^.]+$/, "");
    /* state.previewSrc は画面表示用に縮小されている場合があるため、印刷用の
       実データを作る際は state.hostedUrl（保存先の原本URL）を優先する。
       centerCropPng が内部で原本の dataURL 化を行う。 */
    return centerCropPng(state.hostedUrl || state.previewSrc).then(function (dataUrl) {
      /* 1-A: 生成した切り抜き dataURL を保持しておく。
         この後のモック合成プレビューで Drive から取り直す必要が無くなる */
      state.roundDataUrl = dataUrl;
      mark("丸型: 中央切り抜き生成");
      return uploadDataUrl(dataUrl, "round_" + baseName + ".png", "丸型切り抜き保存");
    }).then(function (json) {
      state.roundUrl = normalizeHostedImageUrl(json.url);
      mark("丸型: 切り抜き保存");
      return state.roundUrl;
    });
  }

  /* ============================================================
     モック合成プレビュー（カート追加時、R2へ参考画像として保存）
     .mock-photo の実際の表示（実写背景 + アップロード画像オーバーレイ）を
     html2canvas で DOM ごとスクリーンショットし、
     "{元ファイル名}_preview.jpeg" として R2 へ追加アップロードする。
     purchase フローには影響させない完全ベストエフォート（失敗時は console.warn のみ）。
     ============================================================ */

  /* html2canvas は初回利用時に asset から遅延読み込みする（通常ページでは未使用のため） */
  var html2canvasPromise = null;
  function loadHtml2Canvas() {
    if (window.html2canvas) return Promise.resolve(window.html2canvas);
    if (html2canvasPromise) return html2canvasPromise;
    if (!CFG.html2canvasUrl) return Promise.reject(new Error("html2canvasの読み込み先が未設定です"));
    html2canvasPromise = new Promise(function (resolve, reject) {
      var s = document.createElement("script");
      s.src = CFG.html2canvasUrl;
      s.onload = function () { resolve(window.html2canvas); };
      s.onerror = function () { html2canvasPromise = null; reject(new Error("html2canvasの読み込みに失敗しました")); };
      document.head.appendChild(s);
    });
    return html2canvasPromise;
  }

  /* 1-D: 空き時間に先読みしておくための薄いラッパー。失敗しても無視する
     （「カートに追加」時に loadHtml2Canvas() が改めて試行するため） */
  function prefetchHtml2Canvas() {
    try { loadHtml2Canvas().catch(function () {}); } catch (e) { /* noop */ }
  }

  function baseFileName(name) {
    return String(name || "image").replace(/\.[^.]+$/, "");
  }

  /* 1-E: プレビューは「参考画像」用途のため、長辺 1000px / JPEG 品質 0.8 に抑える。
     従来（scale = max(2, 1200/幅)）はモバイルで scale 4 前後になり、
     描画・base64 転送・GAS のデコードがいずれも重くなっていた。 */
  var PREVIEW_LONG_EDGE = 1000;
  var PREVIEW_QUALITY = 0.8;

  function previewScaleFor(stageEl) {
    var rect = stageEl.getBoundingClientRect();
    var longEdge = Math.max(1, rect.width, rect.height);
    var scale = PREVIEW_LONG_EDGE / longEdge;
    if (scale < 1) scale = 1;
    if (scale > 3) scale = 3;
    return scale;
  }

  /* html2canvas は object-fit（contain/cover）を解釈できず、img 要素の
     ボックスいっぱいに画像を引き伸ばして描画してしまう（既知の制限）。
     正方形などのモック合成枠に縦長・横長の画像を contain 表示している場合、
     この引き伸ばしにより画像が縦（または横）に潰れて見える。
     撮影直前だけ、実際に画面表示されている見た目どおりのサイズ・位置を
     絶対配置として焼き込み、撮影後に元へ戻す。 */
  function objectFitPositionRatio(computedPosition) {
    var parts = String(computedPosition || "50% 50%").trim().split(/\s+/);
    function ratio(token, zeroKeyword, oneKeyword) {
      if (token === zeroKeyword) return 0;
      if (oneKeyword && token === oneKeyword) return 1;
      if (token === "center") return 0.5;
      var m = /^(-?[\d.]+)%$/.exec(token);
      return m ? parseFloat(m[1]) / 100 : 0.5;
    }
    return {
      x: ratio(parts[0], "left", "right"),
      y: ratio(parts[1], "top", "bottom")
    };
  }

  function neutralizeObjectFitForCapture(stageEl) {
    var restored = [];
    $all("img", stageEl).forEach(function (img) {
      var cs = window.getComputedStyle(img);
      var fit = cs.objectFit;
      if (fit !== "contain" && fit !== "cover") return;
      if (!img.naturalWidth || !img.naturalHeight || !img.offsetParent) return;

      /* 位置の基準は img.offsetParent ではなく直近の親要素にする。
         アクリルキーホルダー（.pm-keybase > .pm-keyparts + .pm-keyphoto > img）
         のように直近の親（.pm-keyphoto）が position 未指定の場合、offsetParent は
         さらに上の .pm-keybase（Flexコンテナ）まで遡ってしまい、img を
         position:absolute にした瞬間に .pm-keyphoto がコンテンツを失って
         つぶれ（min-height:0）、justify-content:center により金具パーツ
         （.pm-keyparts）が下へ再センタリングされてズレていた。 */
      var parent = img.parentElement;
      var parentCs = window.getComputedStyle(parent);
      var imgRect = img.getBoundingClientRect();
      var parentRect = parent.getBoundingClientRect();
      if (!imgRect.width || !imgRect.height) return;

      var scale = fit === "cover"
        ? Math.max(imgRect.width / img.naturalWidth, imgRect.height / img.naturalHeight)
        : Math.min(imgRect.width / img.naturalWidth, imgRect.height / img.naturalHeight);
      var renderW = img.naturalWidth * scale;
      var renderH = img.naturalHeight * scale;
      var pos = objectFitPositionRatio(cs.objectPosition);
      var left = (imgRect.left - parentRect.left) + (imgRect.width - renderW) * pos.x;
      var top = (imgRect.top - parentRect.top) + (imgRect.height - renderH) * pos.y;

      /* img を通常フローから外す前に、直近の親のサイズを現在の見た目のまま
         固定する（Flex 等コンテンツ依存サイズの親がつぶれて兄弟要素が
         ズレるのを防ぐ）。親が position:static の場合のみ relative にし、
         img はその親を基準に絶対配置する。 */
      restored.push({ el: parent, cssText: parent.style.cssText });
      parent.style.width = parentRect.width + "px";
      parent.style.height = parentRect.height + "px";
      if (parentCs.position === "static") parent.style.position = "relative";

      restored.push({ el: img, cssText: img.style.cssText });
      img.style.position = "absolute";
      img.style.left = left + "px";
      img.style.top = top + "px";
      /* 指定した width / height が max-width / max-height / aspect-ratio に
         クランプされると、サイズだけ元に戻って left / top のオフセットだけが
         残り、画像が丸ごとズレて描画される。
         （丸型キーホルダーの .pm-keyphoto img は max-width:78% を持っており、
           実際にこれで写真が左へ約14%ずれていた）
         焼き込む間だけ制約を外す。cssText で一括復元するので影響は撮影中のみ。 */
      img.style.maxWidth = "none";
      img.style.maxHeight = "none";
      img.style.minWidth = "0";
      img.style.minHeight = "0";
      img.style.aspectRatio = "auto";
      img.style.width = renderW + "px";
      img.style.height = renderH + "px";
      img.style.objectFit = "fill";
    });
    return function () {
      restored.forEach(function (r) { r.el.style.cssText = r.cssText; });
    };
  }

  /* .stage を撮影 → JPEG 化 → R2 へ保存。restore は撮影直後に呼ぶ後始末。 */
  function captureStageAndUpload(stageEl, restore) {
    var restoreObjectFit = neutralizeObjectFitForCapture(stageEl);
    return loadHtml2Canvas()
      .then(function (html2canvas) {
        mark("html2canvas 準備");
        return html2canvas(stageEl, {
          backgroundColor: "#ffffff",
          useCORS: true,
          scale: previewScaleFor(stageEl)
        });
      })
      .then(function (canvas) {
        restoreObjectFit();
        restore();
        mark("モック撮影");
        /* toBlob で直接バイナリを得る（toDataURL → base64 の往復が不要） */
        return canvasToBlob(canvas, "image/jpeg", PREVIEW_QUALITY);
      })
      .then(function (blob) {
        mark("JPEG 変換");
        var filename = baseFileName(state.fileName) + "_preview.jpeg";
        return uploadBlob(blob, filename, "プレビュー保存");
      })
      .then(function (json) {
        mark("プレビュー保存");
        return normalizeHostedImageUrl(json.url);
      })
      .catch(function (err) {
        restoreObjectFit();
        restore();
        console.warn("mock preview upload failed:", err);
        return null;
      });
  }

  /* カード（商品）ごとにモック合成プレビューを生成し、GAS へ追加保存する。
     imageUrl はカート追加に使う画像URL（丸型なら切り抜き済みURL）をそのまま渡す。
     戻り値は保存された Drive URL（失敗時は null）で解決する Promise。
     常に resolve するため（reject しない）、呼び出し側は catch 不要で
     line item property（preview_url）へそのまま渡せる。失敗しても購入フロー自体は継続する。

     1-A（高速化）: 画面のスロット img は applyImage() によって既に dataURL が入っている
     （マグはワープ済み canvas の dataURL、丸型は CSS で円形表示）。この場合は差し替え不要なので、
     そのまま撮影する。従来はここで必ず GAS の action:'fetch' を叩いて Drive から原画像
     （最大20MB）を base64 で取り直しており、これがカート追加が遅い最大の原因だった。

     差し替えが必要なのは ?image= からの復元など、スロットにリモートURLが入っている場合のみ。
     その際は canvas の CORS 汚染を避けるため一旦 dataURL 化して差し替え、撮影後に元へ戻す。
     マグカップのみワープ後の canvas dataURL に差し替える（applyMugWarp と同じ手順）。 */
  /* 差し替えた src の読み込み完了を待つ。
     neutralizeObjectFitForCapture() は img.naturalWidth / naturalHeight を見て
     描画サイズを算出するため、src を入れ替えた直後（＝まだ旧画像の寸法のまま）に
     撮影へ進むと、丸型スロットで正方形PNGに差し替えたことが反映されず
     元の長方形写真の寸法で計算されてしまう。必ずここで待ち合わせる。 */
  function waitForImageLoad(img) {
    if (img.complete && img.naturalWidth) return Promise.resolve();
    return new Promise(function (resolve) {
      var done = false;
      function finish() { if (!done) { done = true; resolve(); } }
      img.addEventListener("load", finish, { once: true });
      img.addEventListener("error", finish, { once: true });
      setTimeout(finish, 5000); /* 保険（撮影自体は止めない） */
    });
  }

  /* 丸型スロットの撮影に使う「円形へ切り抜き済みの正方形PNG」を解決する。
     エディタを使った場合は state.roundDataUrl に、使わなかった場合も
     カート追加直前の ensureRoundUrl() が中央切り抜きを生成して同じ変数に
     格納しているため、通常はそのまま返るだけで GAS 往復は発生しない。 */
  function resolveRoundCaptureSrc() {
    if (state.roundDataUrl) return Promise.resolve(state.roundDataUrl);
    if (state.roundUrl) {
      /* エディタが dataUrl を伴わずに保存した場合などの保険 */
      return fetchRemoteAsDataUrl(state.roundUrl).then(function (dataUrl) {
        state.roundDataUrl = dataUrl;
        return dataUrl;
      });
    }
    if (state.hostedUrl || /^data:/.test(state.previewSrc || "")) {
      return centerCropPng(state.hostedUrl || state.previewSrc).then(function (dataUrl) {
        state.roundDataUrl = dataUrl;
        return dataUrl;
      });
    }
    return Promise.resolve(null);
  }

  /* 丸型以外のスロット（Tシャツ・マグ等）に流し込む dataURL を解決する。
     可能な限りローカルの dataURL を使い、GAS 往復を避ける。 */
  function resolvePlainCaptureSrc(imageUrl) {
    if (/^data:/.test(imageUrl)) return Promise.resolve(imageUrl);
    if (/^data:/.test(state.previewSrc || "")) return Promise.resolve(state.previewSrc);
    /* ?image= 復元時のみ。参考画像にすぎないため縮小版で高速化する */
    return fetchRemoteAsDataUrl(imageUrl, 1600);
  }

  function generateAndUploadMockPreview(card, imageUrl) {
    var mockEl = $(".mock-photo", card);
    var stageEl = mockEl && $(".stage", mockEl);
    if (!stageEl || !imageUrl) return Promise.resolve(null);

    var targets = $all("img[data-slot], img[data-slot-round]", stageEl).map(function (img) {
      return { img: img, originalSrc: img.src };
    });

    function restore() {
      targets.forEach(function (t) { t.img.src = t.originalSrc; });
    }

    var roundTargets = targets.filter(function (t) { return t.img.hasAttribute("data-slot-round"); });
    var plainTargets = targets.filter(function (t) { return !t.img.hasAttribute("data-slot-round"); });

    /* 丸型スロットは「常に」切り抜き済みPNGへ差し替えてから撮影する。
       ------------------------------------------------------------
       画面上の丸型モックは、長方形の元写真を CSS（object-fit:cover +
       border-radius:50%）で円形に見せているだけである。一方 html2canvas は
       object-fit を解釈できないため、撮影直前に neutralizeObjectFitForCapture()
       が「見た目どおりの絶対配置」へ焼き込む。このとき元写真が長方形だと
       img 要素自体が横長（cover 相当のサイズ）に広がり、border-radius:50% が
       楕円になってアクリルの縁（padding/border）を覆い隠してしまう。
       これが「エディタを使わずにカート追加したときだけモックと違う」原因。

       エディタ保存後は img.src が 1024x1024 の切り抜き済みPNGになるため
       上記の破綻が起きず、正しいプレビューになっていた。
       そこで未調整の場合も、ensureRoundUrl() が生成済みの同じ正方形PNGへ
       差し替えてから撮影し、両者の結果を完全に一致させる。 */
    var roundSrcPromise = roundTargets.length ? resolveRoundCaptureSrc() : Promise.resolve(null);

    /* 丸型以外は、表示中の src が全て dataURL なら差し替え不要
       （＝ローカルアップロード後の通常ケース） */
    var plainNeedsSwap = plainTargets.some(function (t) {
      return !/^data:/.test(t.img.getAttribute("src") || "");
    });
    var plainSrcPromise = plainNeedsSwap ? resolvePlainCaptureSrc(imageUrl) : Promise.resolve(null);

    return Promise.all([roundSrcPromise, plainSrcPromise]).then(function (srcs) {
      var roundSrc = srcs[0];
      var plainSrc = srcs[1];
      var swapped = [];

      roundTargets.forEach(function (t) {
        if (!roundSrc || t.img.getAttribute("src") === roundSrc) return;
        t.img.src = roundSrc;
        swapped.push(t.img);
      });

      var mugSwaps = [];
      plainTargets.forEach(function (t) {
        if (!plainSrc) return;
        if (t.img.dataset.warp === "mug") {
          mugSwaps.push(new Promise(function (resolve) {
            var probe = new Image();
            probe.crossOrigin = "anonymous";
            probe.onload = function () {
              try { t.img.src = warpHorizontally(probe, true).toDataURL(); }
              catch (e) { t.img.src = plainSrc; }
              swapped.push(t.img);
              resolve();
            };
            probe.onerror = function () { t.img.src = plainSrc; swapped.push(t.img); resolve(); };
            probe.src = plainSrc;
          }));
          return;
        }
        t.img.src = plainSrc;
        swapped.push(t.img);
      });

      return Promise.all(mugSwaps).then(function () { return swapped; });
    }).then(function (swapped) {
      mark(swapped.length ? "画像ソース準備（差し替えあり）" : "画像ソース準備（差し替え不要）");
      /* 差し替えた画像の naturalWidth / naturalHeight が確定してから撮影する */
      return Promise.all(swapped.map(waitForImageLoad));
    }).then(function () {
      return captureStageAndUpload(stageEl, restore);
    }).catch(function (err) {
      restore();
      console.warn("mock preview upload failed:", err);
      return null;
    });
  }

  /* ---------- ファイル選択処理 ---------- */
  var MAX_MB = 20;
  function handleFile(file) {
    if (!file || !/^image\//.test(file.type)) {
      showToast("画像ファイル（JPEG / PNG / HEICなど）を選択してください", true);
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      showToast(MAX_MB + "MB 以下の画像を選択してください", true);
      return;
    }
    var revision = ++imageRevision;
    /* クエリ画像の復元待ち中に選び直された場合は、旧URLの進行表示を即時終了する。
       sticky 自体はこのあと新画像のプレビュー表示に使うため残しておく。 */
    setQueryRestoreStatus(false, true);
    var reader = new FileReader();
    reader.onload = function (e) {
      if (revision !== imageRevision) return;
      var dataUrl = e.target.result;

      /* 新画像を公開する前に旧画像由来のURLと丸型切り抜きを必ず破棄する。
         publishPreview() は hostedUrl を優先するため、この順序が重要。 */
      state.hostedUrl = "";
      state.roundUrl = "";
      state.roundDataUrl = "";
      state.previewSrc = dataUrl;
      state.fileName = file.name;
      applyImage(dataUrl);
      publishPreview();
      syncQuery();

      /* プレビューは applyImage() が新しい画像の中央円形を表示済み */
      var rnote = $("#roundCropNote");
      if (rnote) { rnote.classList.remove("is-done"); rnote.textContent = "中央で自動切り抜き（調整できます）"; }

      var meta = $("#fileMeta");
      if (meta) {
        meta.classList.add("show");
        $("#fmName").textContent = file.name;
        $("#fmSize").textContent = (file.size / 1024 / 1024).toFixed(2) + " MB";
      }
      $all("[data-filename]").forEach(function (el) { el.textContent = file.name; });

      var probe = new Image();
      probe.onload = function () {
        if (revision === imageRevision) checkResolution(probe);
      };
      probe.src = dataUrl;

      var sticky = $("#uploadSticky");
      if (sticky) sticky.classList.add("show");

      showToast("プレビューを全商品に反映しました");
      uploadToWorker(file, revision, function () {});
    };
    reader.readAsDataURL(file);
  }

  function initUpload() {
    var input = $("#fileInput");
    if (!input) return;
    input.addEventListener("change", function () {
      if (input.files.length) handleFile(input.files[0]);
      input.value = ""; /* 同じファイルの再選択にも反応させる */
    });

    $all("[data-open-file]").forEach(function (btn) {
      btn.addEventListener("click", function () { input.click(); });
    });

    var dz = $("#dropzone");
    if (dz) {
      ["dragenter", "dragover"].forEach(function (ev) {
        dz.addEventListener(ev, function (e) { e.preventDefault(); dz.classList.add("dragover"); });
      });
      ["dragleave", "drop"].forEach(function (ev) {
        dz.addEventListener(ev, function (e) { e.preventDefault(); dz.classList.remove("dragover"); });
      });
      dz.addEventListener("drop", function (e) {
        if (e.dataTransfer.files.length) handleFile(e.dataTransfer.files[0]);
      });
    }
  }

  /* ---------- クエリからの復元（?image= & ?client=） ----------
     現行（R2）: Worker が返す公開URLは CORS / CORP の制限が無いため、
     <img src> にそのまま指定できる。GAS を往復して dataURL 化していた
     従来の待ち時間がまるごと無くなり、遷移直後に画像が表示される。
     canvas 加工が必要になった時点で fetchRemoteAsDataUrl() が dataURL 化する。

     以下は旧 Drive URL（既に配布済みのリンク・既存カート）向けの経路:
     Drive の直リンク（drive.usercontent.google.com/download）は
     Cross-Origin-Resource-Policy: same-site が付与されており、このストア
     （別ドメイン）から <img src> 等で直接埋め込むことはブラウザに常時ブロック
     される（一時的な遅延ではなく恒久的な制限。curl や GAS の UrlFetchApp のような
     サーバー間通信には影響しないため気づきにくい）。
     そのため画面表示・canvas 加工には必ず GAS 経由で取得した dataURL を使う
     （fetchRemoteAsDataUrl は既に丸型トリミング等で同じ理由により使われていた）。
     state.hostedUrl には引き続き Drive の直URLを保持し、カートの image_url
     プロパティや解像度判定（metadata）にはそのまま使う（これらはブラウザによる
     埋め込み表示ではないため制限を受けない）。 */
  /* Drive の thumbnailLink（=sNNN）で安定して取得できるのは概ね 1600px までで、
     それを超えるサイズを要求すると縮小版の取得に失敗し、GAS 側が原本
     （最大20MB＝base64で約27MB）を返すフォールバックに落ちる。
     この経路は極端に遅く失敗もしやすいため、要求サイズを 1600 に揃える。
     モック合成プレビュー側も同じ 1600 を使うため、キャッシュも共有される。 */
  var QUERY_RESTORE_DISPLAY_SIZE = 1600; /* 画面プレビュー用途には十分な長辺サイズ */

  /* 画面表示に使える src を返す。R2 の公開URLはそのまま、旧 Drive URL だけ
     GAS 経由で縮小版の dataURL に変換する。 */
  function resolveDisplaySrc(url) {
    if (isDriveUrl(url)) return fetchRemoteAsDataUrl(url, QUERY_RESTORE_DISPLAY_SIZE);
    return Promise.resolve(url);
  }

  function initFromQuery() {
    var p = new URLSearchParams(window.location.search);
    var imageParam = p.get("image");
    var clientParam = p.get("client");
    /* URLSearchParams#get() の時点で既にデコード済みのため、ここで
       decodeURIComponent を重ねて適用しない（二重デコードを避ける） */
    state.client = clientParam || (CFG.defaultClient || "");

    var badge = $("#clientBadge");
    if (badge && clientParam) {
      badge.classList.add("show");
      badge.querySelector("b").textContent = state.client;
    }

    if (imageParam) {
      var restoreRevision = imageRevision;
      var url = normalizeHostedImageUrl(imageParam);
      /* クエリ画像は取得・デコードが終わるまで反映可否が確定しないため、
         アップロード欄に進行中表示を出す。全画面オーバーレイにはせず、
         待機中でも利用者が新しい画像を選び直せるようにする。 */
      setQueryRestoreStatus(true);
      resolveDisplaySrc(url).then(function (displaySrc) {
        return new Promise(function (resolve, reject) {
          var test = new Image();
          /* 寸法を読むだけなので crossOrigin は付けない。付けると CORS ヘッダの
             無い配信先で読み込み自体が失敗し、表示できる画像まで弾いてしまう */
          test.onload = function () { resolve(test); };
          test.onerror = function () { reject(new Error("image decode failed")); };
          test.src = displaySrc;
        }).then(function (test) { return { test: test, displaySrc: displaySrc }; });
      }).then(function (loaded) {
        /* URL画像の取得中に upload パネルで新画像が選ばれた場合は、
           遅れて届いた旧URL画像で表示を戻さない。 */
        if (restoreRevision !== imageRevision) return;
        state.previewSrc = loaded.displaySrc; /* 画面表示に使う src（R2はURL、旧DriveはdataURL） */
        state.hostedUrl = url; /* カート等に渡す実URLは保存先の直URLのまま */
        applyImage(loaded.displaySrc);
        publishPreview();
        if (isDriveUrl(url)) {
          fetchOriginalDimensions(url).then(function (dimensions) {
            if (restoreRevision !== imageRevision) return;
            // metadata非対応の旧GASでは、従来どおり表示画像の寸法へフォールバックする。
            if (dimensions) checkResolution(dimensions);
            else checkResolutionMaybe(loaded.test);
          });
        } else {
          /* R2 は縮小版を挟まず原本をそのまま配信するため、
             いま読み込んだ画像の寸法がそのまま原本の寸法になる。 */
          checkResolution(loaded.test);
        }
        var sticky = $("#uploadSticky");
        if (sticky) sticky.classList.add("show");
        $all("[data-filename]").forEach(function (el) { el.textContent = "アップロード済みの画像"; });
        updateBuyable();
        syncQuery(); /* 旧Drive の表示用URLで来た場合はアドレス欄も原画像URLへ置き換える */
        prefetchHtml2Canvas(); /* 1-D: クリック前に読み込んでおく */
        setQueryRestoreStatus(false, true);
      }).catch(function (err) {
        if (restoreRevision !== imageRevision) return;
        console.error("initFromQuery image load error:", err);
        /* 再試行しても駄目だった場合。通信起因と、URL自体が無効な場合とで
           案内を分ける（前者で「アップロードし直し」を促すのは誤誘導になる） */
        var kind = err && err.pgsKind;
        if (kind === "network" || kind === "timeout" || kind === "format") {
          showToast("画像を読み込めませんでした。通信環境をご確認のうえ、ページを再読み込みしてください", true);
        } else {
          showToast("画像URLが無効です。もう一度アップロードしてください", true);
        }
        setQueryRestoreStatus(false, false);
        updateBuyable();
      });
    } else {
      updateBuyable();
    }
  }
  function checkResolutionMaybe(img) {
    /* 旧 Drive のサムネイルURLは縮小されている場合があるため、
       クエリ復元時は幅が十分あるときだけ判定を表示する */
    if (img.naturalWidth >= 400) checkResolution(img);
  }

  /* ============================================================
     Shopify 商品情報（価格・SKU → バリアントID）
     ============================================================ */
  var productCache = {};
  function getProductJson(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return fetch("/products/" + handle + ".js").then(function (res) {
      if (!res.ok) throw new Error("商品情報を取得できませんでした: " + handle);
      return res.json();
    }).then(function (product) {
      productCache[handle] = product;
      return product;
    });
  }

  function getSelectedSku(card) {
    var select = $(".size_select", card);
    if (select) return String(select.value);
    if (card.dataset.fixedSku) return String(card.dataset.fixedSku);
    return "";
  }

  function resolveVariant(card) {
    var handle = card.dataset.productHandle;
    var sku = getSelectedSku(card);
    return getProductJson(handle).then(function (product) {
      var variant = null;
      if (sku) {
        variant = product.variants.find(function (v) { return String(v.sku) === sku; });
      }
      if (!variant && product.variants.length === 1) variant = product.variants[0];
      return variant || null;
    });
  }

  function updateVariantPrice(card) {
    var priceEl = $(".variant-price", card);
    if (!priceEl) return;
    priceEl.classList.add("is-loading");
    resolveVariant(card).then(function (variant) {
      priceEl.classList.remove("is-loading");
      if (variant) {
        priceEl.innerHTML = "";
        priceEl.appendChild(document.createTextNode(yen(variant.price) + " "));
        var small = document.createElement("small");
        small.textContent = "（税込）";
        priceEl.appendChild(small);
      } else {
        priceEl.textContent = "価格を取得できませんでした";
      }
    }).catch(function (err) {
      console.error(err);
      priceEl.classList.remove("is-loading");
      priceEl.textContent = "価格を取得できませんでした";
    });
  }

  /* 印刷方式（DTF / インクジェット）でサイズ select の SKU を切り替え */
  function initPrintMethodSwitch(card) {
    var printSelect = $(".print_select", card);
    var sizeSelect = $(".size_select", card);
    if (!printSelect || !sizeSelect) return;
    printSelect.addEventListener("change", function () {
      var method = printSelect.value; /* "inkjet" or "dtf" */
      Array.prototype.forEach.call(sizeSelect.options, function (opt) {
        var v = opt.getAttribute("data-" + method);
        if (v) opt.value = v;
      });
      updateVariantPrice(card);
    });
  }

  /* ---------- カートに追加（Shopify Ajax） ---------- */
  function initProductCards() {
    $all("[data-product-card]").forEach(function (card) {
      updateVariantPrice(card);
      initPrintMethodSwitch(card);
      var sizeSelect = $(".size_select", card);
      if (sizeSelect) {
        sizeSelect.addEventListener("change", function () { updateVariantPrice(card); });
      }

      var btn = $(".addToCartBtn", card);
      if (!btn) return;
      btn.addEventListener("click", function () {
        if (btn.disabled) return;
        markReset("クリック");
        var isRound = card.dataset.imageSource === "round";
        if (!isRound && !state.hostedUrl) {
          showToast("先に写真をアップロードしてください", true);
          return;
        }
        var qtyInput = $("input[data-qty]", card);
        var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;

        btn.disabled = true;
        var heldLabel = null;
        /* 丸型エディタを使わずにカート追加した場合、ここで中央切り抜きの生成＋
           GAS への保存が走る。プレビュー生成と同じくそれなりの待ち時間になるため、
           ボタンのラベルだけでなく処理中モーダルも出して操作を止める。 */
        if (isRound && !state.roundUrl) {
          heldLabel = btn.textContent;
          btn.textContent = "切り抜き画像を準備中…";
          setPreviewPreparing(true, "切り抜き画像を保存中…",
            "丸型に切り抜いた画像を作成して保存しています。完了するまで、そのままお待ちください。");
        }
        var prepare = isRound ? ensureRoundUrl() : Promise.resolve(state.hostedUrl);

        prepare.then(function (imageUrl) {
          if (heldLabel) { btn.textContent = heldLabel; heldLabel = null; }
          var prevLabel = btn.textContent;
          btn.textContent = "プレビュー画像を準備中…";
          /* 切り抜き保存から続けてここへ来た場合は、モーダルを出したまま文言だけ切り替わる */
          setPreviewPreparing(true, "プレビュー画像を準備中…",
            "仕上がりイメージを作成して保存しています。完了するまで、そのままお待ちください。");
          return generateAndUploadMockPreview(card, imageUrl).then(function (previewUrl) {
            setPreviewPreparing(false);
            btn.textContent = prevLabel;
            if (!previewUrl) {
              /* モック合成プレビューはあくまで参考画像であり、生成・保存に失敗しても
                 本来の購入フロー（image_url は取得済み）は継続させる。
                 ここで打ち切ると、GAS 側の応答が遅延／不達だっただけのケースでも
                 カート追加ごと失敗扱いになってしまうため */
              console.warn("mock preview unavailable, proceeding without it");
            }
            return resolveVariant(card).then(function (variant) {
              mark("バリアント解決");
              if (!variant) {
                showToast("該当するSKUのバリアントが見つかりません。Shopify管理画面のSKU登録をご確認ください", true);
                return;
              }
              var props = { image_url: normalizeHostedImageUrl(imageUrl), client: state.client };
              if (previewUrl) props.preview_url = previewUrl;
              if (isRound) props.trim = "丸型トリミング済み"; /* 製造側が切り抜き済みと判別できるように */
              return fetch("/cart/add.js", {
                method: "POST",
                headers: { "Content-Type": "application/json", "Accept": "application/json" },
                body: JSON.stringify({
                  id: variant.id,
                  quantity: qty,
                  properties: props
                })
              }).then(function (res) {
                mark("/cart/add.js");
                if (!res.ok) return res.json().then(function (j) { throw new Error(j.description || j.message || "add error"); });
                return res.json();
              }).then(function () {
                showToast("「" + (card.dataset.productName || "商品") + "」をカートに追加しました");
                return refreshCart().then(function () {
                  mark("/cart.js + 再描画");
                  openCart();
                  mark("ドロワー表示");
                  markDump("カート追加");
                });
              });
            });
          }, function (err) {
            setPreviewPreparing(false);
            btn.textContent = prevLabel;
            throw err;
          });
        }).catch(function (err) {
          console.error(err);
          if (isRound && !state.roundUrl) {
            showToast("切り抜き画像を準備できませんでした。" + (err && err.message ? err.message : ""), true);
          } else {
            showToast("カート追加に失敗しました。" + (err && err.message ? err.message : "時間をおいてお試しください"), true);
          }
        }).then(function () {
          setPreviewPreparing(false); /* 想定外の例外時も操作不能状態を残さない */
          if (heldLabel) btn.textContent = heldLabel;
          updateBuyable(); /* disabled 状態を正しい値へ戻す */
          markDump("カート追加（中断/失敗）"); /* 正常時は既に出力済みのため no-op */
        });
      });
    });

    /* 数量ステッパー */
    $all("[data-qty-wrap]").forEach(function (w) {
      var input = $("input", w);
      $("[data-minus]", w).addEventListener("click", function () {
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
      });
      $("[data-plus]", w).addEventListener("click", function () {
        input.value = Math.min(99, (parseInt(input.value, 10) || 1) + 1);
      });
    });
  }

  /* ============================================================
     カートドロワー（Shopify /cart.js 連動）
     ============================================================ */
  function fetchCart() {
    return fetch("/cart.js").then(function (r) { return r.json(); });
  }

  function renderCart(cart) {
    $all("[data-cart-count]").forEach(function (el) { el.textContent = cart.item_count; });
    var body = $("#cdBody");
    if (!body) return;
    body.innerHTML = "";
    if (!cart.items.length) {
      body.innerHTML = "<p class='cd-empty'>カートは空です。<br>お好きな商品を「カートに追加」してください。</p>";
    } else {
      cart.items.forEach(function (it, idx) {
        var line = idx + 1;
        var el = document.createElement("div");
        el.className = "cd-item";
        var previewUrl = it.properties && it.properties.preview_url;
        el.innerHTML =
          "<img class='ci-thumb' alt='' src=''>" +
          "<div><div class='ci-name'></div><div class='ci-opts'></div>" +
          "<div class='ci-price'></div>" +
          "<span class='ci-qty'><button type='button' aria-label='数量を減らす'>−</button><span></span><button type='button' aria-label='数量を増やす'>＋</button></span></div>" +
          "<button class='ci-remove' type='button'>削除</button>";
        var thumbImg = el.querySelector(".ci-thumb");
        if (previewUrl && isDriveUrl(previewUrl)) {
          /* 旧 preview_url（Drive の直URL）は Cross-Origin-Resource-Policy: same-site
             のため、このドメインから <img src> で直接埋め込むことはブラウザに
             ブロックされる。GAS経由で小さめの dataURL 化を行ってから表示する
             （先に商品画像を出し、取得でき次第差し替え） */
          thumbImg.src = it.image || "";
          fetchRemoteAsDataUrl(previewUrl, 300).then(function (dataUrl) {
            thumbImg.src = dataUrl;
          }).catch(function (err) {
            console.warn("cart thumbnail preview fetch failed:", err);
          });
        } else {
          /* R2 の公開URLはそのまま表示できる */
          thumbImg.src = previewUrl || it.image || "";
        }
        el.querySelector(".ci-name").textContent = it.product_title;
        el.querySelector(".ci-opts").textContent =
          (it.variant_title && it.variant_title !== "Default Title") ? it.variant_title : "";
        el.querySelector(".ci-price").textContent = yen(it.final_line_price);
        el.querySelector(".ci-qty > span").textContent = it.quantity;

        var btns = el.querySelectorAll(".ci-qty button");
        btns[0].addEventListener("click", function () { changeLine(el, line, it.quantity - 1); });
        btns[1].addEventListener("click", function () { changeLine(el, line, it.quantity + 1); });
        el.querySelector(".ci-remove").addEventListener("click", function () { changeLine(el, line, 0); });
        body.appendChild(el);
      });
    }
    var tv = $("#cdTotal");
    if (tv) tv.textContent = yen(cart.total_price);
  }

  function changeLine(el, line, quantity) {
    el.classList.add("is-busy");
    fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ line: line, quantity: Math.max(0, quantity) })
    })
      .then(function (r) { return r.json(); })
      .then(renderCart)
      .catch(function (err) {
        console.error(err);
        showToast("カートの更新に失敗しました", true);
        el.classList.remove("is-busy");
      });
  }

  function refreshCart() {
    return fetchCart().then(renderCart).catch(function (err) { console.error(err); });
  }

  function openCart() {
    var d = $("#cartDrawer"), v = $("#drawerVeil");
    if (!d) return;
    d.classList.add("open"); v.classList.add("open");
    d.setAttribute("aria-hidden", "false");
  }
  function closeCart() {
    var d = $("#cartDrawer"), v = $("#drawerVeil");
    if (!d) return;
    d.classList.remove("open"); v.classList.remove("open");
    d.setAttribute("aria-hidden", "true");
  }

  function initCartUi() {
    $all("[data-open-cart]").forEach(function (b) { b.addEventListener("click", function () { refreshCart().then(openCart); }); });
    var c = $("#cdClose"); if (c) c.addEventListener("click", closeCart);
    var v = $("#drawerVeil"); if (v) v.addEventListener("click", closeCart);
    document.addEventListener("keydown", function (e) { if (e.key === "Escape") closeCart(); });
    var go = $("#cdCheckout");
    if (go) go.addEventListener("click", function () { window.location.href = "/checkout"; });
    refreshCart();
  }

  /* ---------- トースト ---------- */
  var toastTimer;
  function showToast(msg, isErr) {
    var t = $("#toast");
    if (!t) return;
    t.innerHTML =
      (isErr
        ? "<svg width='16' height='16' viewBox='0 0 16 16' fill='none' style='color:#FF8FA8'><circle cx='8' cy='8' r='7' stroke='currentColor' stroke-width='1.6'/><path d='M8 4.5v4.4M8 11.4v.2' stroke='currentColor' stroke-width='1.6' stroke-linecap='round'/></svg>"
        : "<svg width='16' height='16' viewBox='0 0 16 16' fill='none'><circle cx='8' cy='8' r='7' stroke='currentColor' stroke-width='1.6'/><path d='M5 8.2l2 2 4-4.4' stroke='currentColor' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round' fill='none'/></svg>") +
      "<span></span>";
    t.querySelector("span").textContent = msg;
    t.classList.add("show");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { t.classList.remove("show"); }, 2800);
  }

  /* ---------- モバイルナビ ---------- */
  function initNav() {
    // document.querySelector に変更することで動作を安定化させます
    var btn = document.querySelector("#navToggle");
    var nav = document.querySelector("#mainNav");
    
    if (!btn || !nav) return;
    
    btn.addEventListener("click", function () {
      var open = nav.classList.toggle("open");
      btn.setAttribute("aria-expanded", open ? "true" : "false");
    });
  }

  /* ---------- 丸型トリミングエディタ連携 ----------
     pgs-round-editor.js が切り抜き画像を GAS へ保存すると
     'pgs:roundcrop' イベント（detail: {url, dataUrl}）を発火する。 */
  function initRoundCrop() {
    document.addEventListener("pgs:roundcrop", function (e) {
      var d = e.detail || {};
      if (!d.url) return;
      state.roundUrl = d.url;
      /* 1-A: 調整済み切り抜きの dataURL を保持し、カート追加時の GAS 往復を省く */
      state.roundDataUrl = d.dataUrl || "";
      /* 商品ナビと商品カードなど、同じ丸型モックが複数あるため全件を同期する。 */
      $all("img[data-slot-round]").forEach(function (img) {
        img.src = d.dataUrl || d.url; /* 即時表示は dataURL、カートには Drive URL */
      });
      var rnote = $("#roundCropNote");
      if (rnote) { rnote.classList.add("is-done"); rnote.textContent = "✔ 調整した切り抜きを使用します"; }
      updateBuyable();
      showToast("丸型に切り抜いた画像を保存しました");
    });
  }

  /* ---------- init ---------- */
  document.addEventListener("DOMContentLoaded", function () {
    applyImage(state.previewSrc);
    publishPreview();
    initFromQuery();
    initUpload();
    initProductCards();
    initCartUi();
    initNav();
    initRoundCrop();
  });
})();
